// The opening-hours summary of the full contact block (UserFlows §2.3), ported from the mockup's
// `lib/clinicHours.ts` `hoursSummary`: consecutive days with the same hours are grouped, Monday first.

export interface DayHours {
  weekday: number
  open: boolean
  start: string
  end: string
}

const SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
/** Weekdays in the order clinics think about them: Monday first. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]

/** `"08:00"` → `"8:00 AM"`, `"12:00"` → `"12:00 PM"`, `"24:00"` → `"12:00 AM"`. */
export function formatTime(time: string): string {
  const [h = 0, m = 0] = time.split(':').map(Number)
  const suffix = h < 12 || h === 24 ? 'AM' : 'PM'
  return `${String(h % 12 || 12)}:${String(m).padStart(2, '0')} ${suffix}`
}

const same = (a: DayHours, b: DayHours) => a.open === b.open && (!a.open || (a.start === b.start && a.end === b.end))

/** `["Mon–Fri 8:00 AM – 5:00 PM", "Sat 8:00 AM – 12:00 PM", "Sun Closed"]` from the 7 `DayHours` rows. */
export function hoursSummary(hours: readonly DayHours[]): string[] {
  const byDay = new Map(hours.map((h) => [h.weekday, h]))
  const at = (weekday: number): DayHours => byDay.get(weekday) ?? { weekday, open: false, start: '', end: '' }
  const out: string[] = []
  for (let i = 0; i < WEEK_ORDER.length;) {
    const day = WEEK_ORDER[i] ?? 0
    let j = i
    while (j + 1 < WEEK_ORDER.length && same(at(WEEK_ORDER[j + 1] ?? 0), at(day))) j++
    const last = WEEK_ORDER[j] ?? day
    const days = i === j ? SHORT[day] : `${SHORT[day] ?? ''}–${SHORT[last] ?? ''}`
    const h = at(day)
    out.push(`${days ?? ''} ${h.open ? `${formatTime(h.start)} – ${formatTime(h.end)}` : 'Closed'}`)
    i = j + 1
  }
  return out
}
